/**
 * HTTP client for the AI Clinical Copilot service (`ai_assist/`).
 *
 * Deliberately separate from `apiClient`. The copilot runs as its own service
 * on its own origin — every backend change for this feature is confined to
 * `ai_assist/`, so it is not reachable through the portal's `/api/proxy/$`
 * pass-through and owns its own CORS allowlist instead.
 *
 * What it shares with `apiClient` is the part that matters: the same Firebase
 * ID token, minted the same way and refreshed on the same 401-once rule. The
 * copilot service verifies that token's signature itself and then replays it
 * against the main backend to authorize the caller, so a doctor's access here
 * can never exceed their access there.
 */

import { ensureFirebaseAuth } from "@/lib/firebase";
import { log } from "@/lib/logger";

import { ApiError } from "./client";

const CONFIGURED = import.meta.env["VITE_AI_ASSIST_URL"] as string | undefined;

/** Default matches `AI_ASSIST_PORT`'s default in `ai_assist/.env.example`. */
export const AI_ASSIST_BASE =
  (CONFIGURED ?? "http://localhost:4100").replace(/\/+$/, "") + "/api/ai";

/**
 * Whether the copilot is configured at all.
 *
 * A deployment that has not stood the service up yet is a supported state, not
 * a broken one — the call screen simply does not offer the panel.
 */
export const copilotConfigured = Boolean(CONFIGURED) || import.meta.env.DEV;

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal | undefined;
}

async function authHeader(forceRefresh = false): Promise<Record<string, string>> {
  const auth = await ensureFirebaseAuth();
  const user = auth?.currentUser;
  if (!user) return {};
  return { Authorization: `Bearer ${await user.getIdToken(forceRefresh)}` };
}

async function send<T>(path: string, options: RequestOptions, retry: boolean): Promise<T> {
  const { method = "GET", body, signal } = options;
  const done = log.start("copilot", "call", { method, path, ...(retry ? { retry: true } : {}) });

  let response: Response;
  try {
    response = await fetch(`${AI_ASSIST_BASE}${path}`, {
      method,
      signal: signal ?? null,
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(await authHeader(retry)),
      },
      body: body !== undefined ? JSON.stringify(body) : null,
    });
  } catch (error) {
    const aborted = signal?.aborted || (error as Error)?.name === "AbortError";
    if (aborted) done({ level: "debug", event: "call.aborted" });
    else done({ level: "warn", event: "call.failed", error });
    // The copilot being unreachable is a degradation, never a page failure —
    // so this is logged at warn, not error, and every caller tolerates it.
    throw new ApiError(0, "The AI copilot service is unavailable.");
  }

  const raw = await response.text();
  let payload: unknown = null;
  try {
    payload = raw ? JSON.parse(raw) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    if (response.status === 401 && !retry) {
      done({ level: "debug", event: "call.retry", status: 401 });
      return send<T>(path, options, true);
    }
    const message =
      (payload as { message?: string } | null)?.message ??
      "The AI copilot could not complete that request.";
    done({
      level: response.status >= 500 ? "warn" : "debug",
      status: response.status,
      msg: message,
    });
    throw new ApiError(response.status, message);
  }

  done({ status: response.status });
  return payload as T;
}

export const aiAssistClient = {
  get: <T>(path: string, signal?: AbortSignal) => send<T>(path, { signal }, false),
  post: <T>(path: string, body?: unknown) => send<T>(path, { method: "POST", body }, false),
  patch: <T>(path: string, body?: unknown) => send<T>(path, { method: "PATCH", body }, false),
};

/** A 404 from the copilot means "nothing yet", which is rarely an error. */
export const isNotFound = (error: unknown) => error instanceof ApiError && error.status === 404;
/** Status 0 is this client's marker for "the service did not answer at all". */
export const isUnreachable = (error: unknown) => error instanceof ApiError && error.status === 0;
